import { useCallback, useEffect, useRef, useState } from "react";

export const HOLIDAY_SOURCE_BASE_URL = "https://date.nager.at/api/v3/PublicHolidays";
export const UK_HOLIDAY_COUNTRY_CODE = "GB";
export const SG_HOLIDAY_COUNTRY_CODE = "SG";

const UK_NATIONS = ["GB-ENG", "GB-WLS", "GB-NIR"];

function buildHolidayEvent(holiday, countryCode) {
  const dateIso = String(holiday?.date || "").trim();
  if (!dateIso) return null;

  // Skip GB holidays that apply only to Scotland and not to England/Wales/NI.
  if (countryCode === UK_HOLIDAY_COUNTRY_CODE) {
    const counties = holiday?.counties;
    if (Array.isArray(counties) && counties.length > 0) {
      if (!counties.some((c) => UK_NATIONS.includes(c))) {
        return null;
      }
    }
  }

  const holidayName =
    String(holiday?.localName || "").trim() ||
    String(holiday?.name || "").trim() ||
    "Public Holiday";
  const countryLabel = countryCode === SG_HOLIDAY_COUNTRY_CODE ? "SG" : "UK";

  const nextDate = new Date(`${dateIso}T00:00:00`);
  nextDate.setDate(nextDate.getDate() + 1);
  const nextDateIso = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, "0")}-${String(nextDate.getDate()).padStart(2, "0")}`;

  return {
    id: `holiday-${countryCode}-${dateIso}`,
    title: `${countryLabel}: ${holidayName}`,
    start: dateIso,
    end: nextDateIso,
    allDay: true,
    editable: false,
    startEditable: false,
    durationEditable: false,
    classNames: [countryCode === SG_HOLIDAY_COUNTRY_CODE ? "holiday-event-sg" : "holiday-event-uk"],
    extendedProps: {
      isHoliday: true,
      holidayCountry: countryCode,
    },
  };
}

function yearsInRange(startValue, endValue) {
  const start = startValue instanceof Date ? startValue : new Date(startValue || new Date());
  const end = endValue instanceof Date ? endValue : new Date(endValue || new Date());
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return [new Date().getFullYear()];
  }

  const firstYear = Math.min(start.getFullYear(), end.getFullYear());
  const lastYear = Math.max(start.getFullYear(), end.getFullYear());
  const years = [];
  for (let year = firstYear; year <= lastYear; year += 1) {
    years.push(year);
  }
  return years;
}

export function useHolidayEvents({ startDate, endDate, showUkHolidays, showSingaporeHolidays }) {
  const [holidayEvents, setHolidayEvents] = useState([]);
  const [holidayError, setHolidayError] = useState("");
  const holidayCacheRef = useRef(new Map());
  const holidayRequestRef = useRef(new Map());

  const fetchHolidaysForYear = useCallback(async (countryCode, year) => {
    const cacheKey = `${countryCode}-${year}`;
    if (holidayCacheRef.current.has(cacheKey)) {
      return holidayCacheRef.current.get(cacheKey);
    }

    if (holidayRequestRef.current.has(cacheKey)) {
      return holidayRequestRef.current.get(cacheKey);
    }

    const request = fetch(`${HOLIDAY_SOURCE_BASE_URL}/${year}/${countryCode}`)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Holiday feed error (${response.status})`);
        }

        const payload = await response.json();
        const entries = Array.isArray(payload) ? payload : [];
        holidayCacheRef.current.set(cacheKey, entries);
        return entries;
      })
      .finally(() => {
        holidayRequestRef.current.delete(cacheKey);
      });

    holidayRequestRef.current.set(cacheKey, request);
    return request;
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const loadHolidays = async () => {
      const countryCodes = [];
      if (showUkHolidays) countryCodes.push(UK_HOLIDAY_COUNTRY_CODE);
      if (showSingaporeHolidays) countryCodes.push(SG_HOLIDAY_COUNTRY_CODE);

      if (countryCodes.length === 0) {
        setHolidayEvents([]);
        setHolidayError("");
        return;
      }

      const years = yearsInRange(startDate, endDate);
      const requests = [];
      for (const countryCode of countryCodes) {
        for (const year of years) {
          requests.push(
            fetchHolidaysForYear(countryCode, year).then((entries) => ({ countryCode, entries }))
          );
        }
      }

      const settled = await Promise.allSettled(requests);
      if (isCancelled) return;

      const allEvents = [];
      let hadFeedError = false;

      for (const result of settled) {
        if (result.status !== "fulfilled") {
          hadFeedError = true;
          continue;
        }

        const { countryCode, entries } = result.value;
        for (const holiday of entries) {
          const holidayEvent = buildHolidayEvent(holiday, countryCode);
          if (holidayEvent) {
            allEvents.push(holidayEvent);
          }
        }
      }

      const uniqueById = new Map();
      allEvents.forEach((eventItem) => uniqueById.set(eventItem.id, eventItem));
      setHolidayEvents(Array.from(uniqueById.values()));
      setHolidayError(hadFeedError ? "Some holidays could not be loaded." : "");
    };

    loadHolidays();

    return () => {
      isCancelled = true;
    };
  }, [endDate, fetchHolidaysForYear, showSingaporeHolidays, showUkHolidays, startDate]);

  return { holidayEvents, holidayError };
}
